import React, { useState, useEffect } from 'react';
import { Popover } from '@barges/react';
import classes from './classes.module.scss';
import RateSection from './rate_section';
import ReviewSection from './review_section';
import withPGTranslation from '../../config/withPGTranslation';
import Const, { rateReviewTheme } from '../../config/const';
import Alert from '../../../common/helpers/alert';
import { paramOrNul } from '../../../common/config/utils';
import { getSessionType } from '../../actions/analytics';
import { useNotification } from '../firebase_messaging_provider';

const checkCanPostLikeComment = (like, comment, t) => {
  let canPost = true;
  const errors = [];
  if (like === null) {
    errors.push(t('rate_review.please_rate_the_advisor'));
    canPost = false;
  }
  if (!like && comment.length < 10) {
    errors.push(t('rate_review.please_provide_some_details_about_your_experience'));
    canPost = false;
  }
  if (like && comment.length < 3) {
    errors.push(t('rate_review.please_provide_some_details_about_your_experience'));
    canPost = false;
  }
  return { canPost, errors };
};

const checkCanPost = (like, comment, setAlerts, t) => {
  const { canPost, errors } = checkCanPostLikeComment(like, comment, t);
  if (!canPost) {
    const uniqErrors = [...new Set(errors)];
    setAlerts([...uniqErrors]);
  }
  return canPost;
};

const getFeedbackData = (props) => {
  const {
    like, comment, xfm, orderId, ppm, undiscountedPpm, advisorName,
    clickSource,  advisorId, totalCharged, chatType, discountValue
  } = props;
  const totalChargedNumber = totalCharged ? Number(totalCharged) : undefined;
  const feedbackData = {
    ...paramOrNul('advisor name', advisorName),
    ...paramOrNul('ppm', ppm),
    ...paramOrNul('undiscounted ppm', undiscountedPpm),
    ...paramOrNul('session amount', totalChargedNumber),
    ...paramOrNul(`${ getSessionType(chatType) } charged`, totalChargedNumber || totalCharged),
    ...paramOrNul('xfm', xfm),
    ...paramOrNul('discount value', discountValue),
    ...paramOrNul('click source', clickSource),
    ...paramOrNul('advisor id', advisorId),
    ...paramOrNul('order id', orderId),
    ...paramOrNul('sentiment', like),
    ...paramOrNul('review length', comment.length),
    ...paramOrNul('chat call', chatType === Const.chatType.text),
    ...paramOrNul('voice call', chatType === Const.chatType.voice),
    ...paramOrNul('video call', chatType === Const.chatType.video)
  };
  return feedbackData;
};

const checkSendFeedback = async (props) => {
  const {
    like, comment, sendOrderReview, orderId, setAlerts, t, onCancel, redirectTo
  } = props;
  if (!checkCanPost(like, comment, setAlerts, t)) return;
  const feedback = { feedbackVote: like, feedbackComment: comment };
  const feedbackData = getFeedbackData({ ...props });
  await sendOrderReview(orderId, feedback, feedbackData);
  onCancel(like);
  if (redirectTo) redirectTo();
};

const alertsContent = (alerts) => {
  if (alerts.length > 0) {
    return alerts.map((alert, index) => <Alert key={ index } message="" errorString={ alert } banner />);
  }
  return null;
};

const redirectIfNeed = ({ redirectTo }) => {
  if (redirectTo) redirectTo();
};

function RateReview({
  visible,
  onCancel,
  orderId,
  advisorName,
  sendOrderReview,
  totalCharged,
  existingComment = '',
  existingLike,
  t,
  clickSource,
  advisorId,
  chatType,
  redirectTo,
  theme = rateReviewTheme.DEFAULT,
  analytics,
  loading
}) {
  const [like, setLike] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [comment, setComment] = useState('');
  const { requestPermission } = useNotification();

  useEffect(() => {
    setLike(existingLike);
    setComment(existingComment);
  }, [existingLike, existingComment]);

  useEffect(() => {
    if (alerts.length === 0 && shouldValidate) {
      checkSendFeedback({
        like, comment, sendOrderReview, orderId, advisorName, setAlerts, t, chatType, clickSource,
        advisorId, onCancel, redirectTo, totalCharged, ...analytics
      });
      setShouldValidate(false);
    }
  }, [alerts, shouldValidate]);

  const onSkipClick = () => {
    setLike(existingLike);
    setComment(existingComment);
    setAlerts([]);
    onCancel();
    redirectIfNeed({ redirectTo });
  };

  const onSubmit = (c) => {
    setComment(c);
    if (alerts.length > 0) {
      setAlerts([]);
      setShouldValidate(true);
      return;
    }
    if (like === true && clickSource !== 'order details') {
      requestPermission();
    }

    checkSendFeedback({
      like, comment: c, sendOrderReview, orderId, advisorName, chatType, setAlerts, t, clickSource,
      advisorId, onCancel, redirectTo, totalCharged, ...analytics
    });
  };

  const renderRateReviewDEFAULT = () => (
    <Popover
      isOpen={ visible }
      onCloseButtonClick={ onCancel }
      centered
      className={ classes.rateReviewPopover }
    >
      <div className={ classes.rateReviewContainer }>
        {alertsContent(alerts)}
        <RateSection value={ like } onValueChanged={ (liked) => setLike(liked) } />
        <ReviewSection
          comment={ comment }
          advisorName={ advisorName }
          onSubmit={ onSubmit }
          loading={ loading }
        />
        <div className={ classes.skipButtonContainer }>
          <button type="button" className={ classes.skipButton } onClick={ onSkipClick } disabled={ loading }>
            {t('rate_review.skip')}
          </button>
        </div>
      </div>
    </Popover>

  );

  const renderRateReviewCHATENDED = () => (
    <div className={ classes.rateReviewCHATENDED }>
      {alertsContent(alerts)}
      <RateSection
        value={ like }
        onValueChanged={ (liked) => setLike(liked) }
        theme={ rateReviewTheme.CHAT_ENDED }
      />
      <ReviewSection
        comment={ comment }
        advisorName={ advisorName }
        onSubmit={ onSubmit }
        loading={ loading }
      />
    </div>
  );

  return (
    <div>
      { { renderRateReviewDEFAULT, renderRateReviewCHATENDED }[`renderRateReview${ theme }`]() }
    </div>
  );
}

export default withPGTranslation(RateReview);
